import { getMarket, getMarketCode } from '@/utils/env';
import type { MarketMetadata } from '../@types/MarketMetadata';
import type { MarketCode } from '../@types/Token';
import type { OdometerUnit } from '../data/vehicles/useVehicle';
import marketsMetadata from '../marketsMetadata.json';
import { getPreferredLocale, type SupportedLocale } from './locale-cookie';
import { PrettyNumber } from './string-formatter';

// Re-export getMarketCode and getMarket from env.ts for backwards compatibility
export { getMarket, getMarketCode };

/**
 * Get the language/locale for a given market code
 * Does NOT check locale cookie - use getLanguageWithLocaleOverride for that
 */
export const getLanguage = (marketCode: MarketCode): SupportedLocale => {
  switch (marketCode) {
    case 'NO':
      return 'nb-NO';
    case 'SE':
      return 'sv-SE';
    default:
      return 'sv-SE'; // Default to Swedish
  }
};

/**
 * Get the language/locale, checking cookie override first, then falling back to market code
 */
export const getLanguageWithLocaleOverride = (marketCode: MarketCode): SupportedLocale => {
  const preferredLocale = getPreferredLocale();
  if (preferredLocale) {
    return preferredLocale;
  }
  return getLanguage(marketCode);
};

/**
 * Get the HTML lang attribute value for a given locale
 */
export const getHtmlLangFromLocale = (locale: SupportedLocale): string => {
  return locale.split('-')[0]; // sv-SE -> sv, nb-NO -> nb, fi-FI -> fi
};

/**
 * Get the HTML lang attribute value for a given market code
 * Does NOT check locale cookie - use getHtmlLangWithLocaleOverride for that
 */
export const getHtmlLang = (marketCode: MarketCode): string => {
  const locale = getLanguage(marketCode);
  return getHtmlLangFromLocale(locale);
};

/**
 * Get the HTML lang attribute, checking cookie override first
 */
export const getHtmlLangWithLocaleOverride = (marketCode: MarketCode): string => {
  const locale = getLanguageWithLocaleOverride(marketCode);
  return getHtmlLangFromLocale(locale);
};

export const getAllMarketMetadata = () => marketsMetadata;

export const getExternalView = () => getMarket()?.externalView;

export const getDefaultOdometerUnit = (marketCode?: MarketCode): OdometerUnit => {
  switch (marketCode) {
    case 'NO':
      return 'km';
    //case 'SE':
    default:
      return 'mil';
  }
};

export const getMarketMetadata = (): MarketMetadata => {
  const market = getMarket();
  return marketsMetadata[market?.marketCode ?? 'US'];
};

export const getMarketplace = (): string | undefined => {
  const market = getMarket();
  return market?.marketplace;
};

export const getDealer = () => getMarket()?.dealer;

export const marketCurrency = (value?: number | string) => {
  const { currencySymbol, currencySymbolBefore } = getMarketMetadata();
  const transformedValue =
    typeof value === 'number' || typeof value === 'string' ? PrettyNumber(value) : '-';
  if (currencySymbolBefore) return `${currencySymbol} ${transformedValue}`;
  return `${transformedValue} ${currencySymbol}`;
};
